const _ = require('lodash');

class CartControlsController {
    constructor($element, $timeout, $q, $state, moveFocus, trolleyService, analyticsService) {
        this.$element = $element;
        this.$timeout = $timeout;
        this.$q = $q;
        this.$state = $state;
        this.moveFocus = moveFocus;
        this.trolleyService = trolleyService;
        this.analyticsService = analyticsService;

        this.updateInTrolley = _.debounce(this.updateInTrolley, 500);
    }

    $onInit() {
        this.product = this.trolleyProduct ? this.trolleyProduct : this.shelfProduct;
        this.isInKilograms = this.product.Unit === 'KG';
        this.defaultQuantity = this.isInKilograms ? this.product.MinimumQuantity : 1;

        this.buttonPostfix = this.isInKilograms ?
            ` quantity by ${this.product.MinimumQuantity} kg.` :
            ' quantity.';
    }

    $onChanges() {
        this.product = this.trolleyProduct ? this.trolleyProduct : this.shelfProduct;

        if (this.shelfProduct) {
            this.shelfProduct.IsInTrolley = !!this.trolleyProduct;
        }
    }

    $onDestroy() {
        if (this.product.IsDeliveryPass) {
            this.trolleyService.deliveryPassLimitReached[this.product.Stockcode] = false;
            this.trolleyService.showDCPdeliveryPassLimitReachedMsg = false;
        }
    }

    /**
     * Performs validation of the new quantity and performs the quantity update
     * if validation succeeds. This method is the entry point and is the only
     * method which should be used to add/update/remove an item in the cart.
     *
     * @param {number} newQuantity
     * @param {boolean} [allowRemove=false] Indicates whether the product can be removed.
     *  When true, if the new quantity is zero, the product is removed from the trolley.
     * @returns {Promise<any>}
     *
     * @memberOf CartControlsController
     */
    validateAndUpdateQuantity(newQuantity, allowRemove = false) {
        if (newQuantity == null) {
            // The user has backspaced, we must wait for more input.
            return this.$q.reject();
        }

        newQuantity = this.correctRoundingErrors(newQuantity);

        if (allowRemove && newQuantity === 0) {
            return this.removeFromTrolley(this.product);
        }

        return this.$q.all({
            validationResult: this.validateQuantity(newQuantity),
            updateResponse: this.updateQuantity(newQuantity),
        }).then(result => this.onValidateAndUpdate(result));
    }

    onValidateAndUpdate({ validationResult, updateResponse }) {
        if (updateResponse) {
            this.checkForMessages(updateResponse.Message);
        }

        if (this.onError && !validationResult.isValid) {
            this.$timeout(() => this.onError({ reason: validationResult.reason }));
        } else if (this.onSuccess && validationResult.isValid) {
            this.$timeout(() => this.onSuccess());
        }
    }

    validateQuantity(newQuantity) {
        return this.validateDeliveryPassLimit(this.product, newQuantity)
            .then(() => this.trolleyService.validateQuantity(this.product, newQuantity));
    }

    updateQuantity(newQuantity) {
        // This is a dangerous hack that prevents the trolley response
        // coming back and resetting the quantity while the user is still
        // clicking the plus button. It will make the trolley service
        // throw away responses from previous update requests.
        this.trolleyService.updateRequestCount++;

        const updatePromise = this.product.IsInTrolley ?
            this.updateInTrolley(this.product, newQuantity) :
            this.addToTrolley(this.product, newQuantity);

        // Optimistically update the quantity.
        this.product.QuantityInTrolley = newQuantity;

        return updatePromise;
    }

    updateInTrolley(product, quantity) {
        return this.trolleyService.updateTrolleyItem(product, quantity, this.source);
    }

    addToTrolley(product, quantity) {
        this.product.IsInTrolley = true;
        this.moveFocus(() => this.$element.find('._inTrolleyFocus'));

        return this.trolleyService.addItemToTrolley(product, quantity, this.source, this.$state.params.searchTerm);
    }

    removeFromTrolley(product) {
        if (this.onRemove) {
            this.onRemove();
        }

        // If there are any pending debounced quantity updates (e.g. from the user decrementing)
        // we want to cancel those since the product is being removed.
        this.updateInTrolley.cancel();

        this.product.IsInTrolley = false;
        this.moveFocus(() => this.$element.find('._outOfTrolleyFocus'));

        return this.trolleyService.removeTrolleyItem(product.Stockcode);
    }

    correctRoundingErrors(quantity) {
        // correct for floating point rounding errors
        return this.isInKilograms ? parseFloat(quantity.toFixed(2)) : quantity;
    }

    validateDeliveryPassLimit(product, quantity) {
        if (!product.IsDeliveryPass) {
            return this.$q.when(true);
        }

        this.trolleyService.deliveryPassLimitReached[product.Stockcode] = false;
        this.trolleyService.showDCPdeliveryPassLimitReachedMsg = false;

        return this.trolleyService.validateDeliveryPassLimit(product, quantity)
            .then(response => this.onDeliveryPassLimitValidation(product, quantity, response.NewQuantity));
    }

    onDeliveryPassLimitValidation(product, newQuantity, validatedQuantity) {
        if (newQuantity === validatedQuantity) {
            return;
        }

        this.trolleyService.deliveryPassLimitReached[product.Stockcode] = true;
        this.trolleyService.showDCPdeliveryPassLimitReachedMsg = !!this.shelfProduct;

        product.QuantityInTrolley = validatedQuantity;
        product.IsInTrolley = validatedQuantity > 0;
    }

    checkForMessages(message) {
        // If the response has a message, show a dialog with the message.
        if (message && !this.$state.is('checkout')) {
            this.modalMessage = message;
            this.analyticsService.pushError(message);
        }
    }

    resetModalMessage() {
        this.modalMessage = null;
        this.moveFocus(this.$element.find('._inTrolleyFocus'));
    }
}

CartControlsController.$inject = ['$element', '$timeout', '$q', '$state', 'MoveFocus', 'TrolleyService', 'AnalyticsService'];

const cartControlsComponentDefinition = {
    bindings: {
        trolleyProduct: '<',
        shelfProduct: '<?',
        source: '@',
        classModifier: '@?',
        onSuccess: '&?',
        onError: '&?',
        onRemove: '&?',
    },
    controller: CartControlsController,
    template: require('./cart-controls.component.html').trim(),
};

module.exports = {
    CartControlsController,
    cartControlsComponentDefinition,
};



// WEBPACK FOOTER //
// ./src/shop/cart/cart-controls.component.js